import * as calculatorConfig from '@/config/base-config.json';
import { FINANCING_YEAR_65, FINANCING_YEAR_75, FinancingLoanType } from '@/constants/common';
import { generateSliderSteps } from '@/utils/generateSliderSteps';
import * as config from '@/config/config.json';

export const PURCHASE_NEW_PROJECT_LABEL = {
  PurchaseNewProject: 'Purchase New Project (BUC)',
  PurchaseDetail: 'Purchase Details',
  PurchasePrice: 'Purchase Price',

  // Potential Expenses
  PotentialExpenses: 'Potential Expenses',
  BSDLongForm: "Buyer's Stamp Duty",
  BSD: 'BSD',
  ABSDLongForm: "Additional Buyer's Stamp Duty",
  ABSD: 'ABSD',
  BsdAndAbsdNote:
    'BSD/ABSD has to be paid upfront in cash before reimbursement from CPF can occur if there are sufficient funds in CPF.',
  LegalFee: 'Legal Fee',

  // Housing Loan
  HousingLoan: 'Housing Loan',
  BankLoan: 'Bank Loan',
  NoLoan: 'No Loan',
  NumberOfExistingLoans: 'Number of Existing Housing Loans',
  NumberOfExistingLoansTooltip: 'Select the number of existing housing loans you currently have.',
  Optional: 'Optional',

  // Financing Variables
  FinancingVariables: 'Financing Variables',
  LoanTenure: 'Loan Tenure (Years)',
  MortgageLoanInterestRate: 'Mortgage Loan Interest Rate',
  LoanToValue: 'Loan-to-Value',

  // Breakdown
  BreakdownOfLoan: 'Breakdown of Loan',
  Cash: 'Cash',
  CpfOaPerCash: 'CPF OA / Cash',
  LoanAmount: 'Loan Amount',
  MonthlyPayable: 'Monthly Payable',
  Principal: 'Principal',
  Interest: 'Interest',
  MonthlyPayableNote: 'Estimation for the first payment month only upon CSC',

  // Progressive Payment
  ProgressivePaymentEstimates: 'Progressive Payment Estimates',
  ProgressivePaymentSubtitle: 'Subject to Construction Method & BCA Application',
  Stages: 'Stages',
  Timeline: 'Timeline (Months)',
  Payment: 'Payment (%)',
  TotalAmount: 'Total Amount',
  Outlay: 'Outlay (Cash/CPF)',
  Loan: 'Loan Disbursed',
  MonthlyPayment: 'Cumulative Monthly Payment',
  Total: 'Total',

  // Progressive Payment Stages
  OptionFee: 'Option Fee',
  OptionFeeSubtitle: 'Receive Option to Purchase (OTP)',
  BalanceDownpayment: 'Balance Downpayment',
  BalanceDownpaymentSubtitle: '8 weeks from Option to Purchase',
  Foundation: 'Foundation',
  FoundationSubtitle: 'Completion of foundation work',
  Framework: 'Framework',
  FrameworkSubtitle: 'Completion of reinforced concrete framework',
  Wall: 'Wall',
  WallSubtitle: 'Completion of partition walls',
  Ceiling: 'Ceiling',
  CeilingSubtitle: 'Completion of roofing/ceiling',
  Windows: 'Windows',
  WindowsSubtitle: 'Completion of door, window frames & plumbing',
  CarPark: 'Car Park',
  CarParkSubtitle: 'Completion of car park, roads & drains',
  TOP: 'TOP',
  TOPSubtitle: 'Temporary Occupation Permit',
  CSC: 'CSC',
  CSCSubtitle: 'Legal Completion',

  // Progressive Payment Note
  ProgressivePaymentNote: 'If OTP is not exercised by the deadline, a 1.25% cancellation penalty',
  ProgressivePaymentNoteWillBeRefunded: 'applies and the remaining 3.75%',
  ProgressivePaymentNoteRefundable: 'will be refunded.',

  // Purchase Outlay Breakdown
  PurchaseOutlayBreakdown: 'Purchase Outlay Breakdown',
  OutstandingPayablesInCashOnly: 'Outstanding Payables in Cash Only',
  OutstandingPayablesInCpfOaPerCash: 'Outstanding Payables in CPF OA / Cash',
  TotalOutlay: 'Total Outlay',

  // Purchase Timeline
  PurchaseTimeline: 'Purchase Timeline',
  OTPLongForm: 'Option to Purchase',
  OTP: 'OTP',
  ReceiveOfSalesAndPurchase: 'Receive of Sales & Purchase',
  ExerciseOfSalesAndPurchase: 'Exercise of Sales & Purchase',
  ProgressiveDownpayment: 'Progressive Downpayment',
  CompletionOfFoundationWork: 'Completion of Foundation Work',
  Weeks: 'Weeks',
  Months: 'Months',
};

// Progressive Payment Stages Configuration
export const progressivePaymentConfig =
  config.payment_progressive_plan_in_percent.private_resale.purchase_new_project;

export const PROGRESSIVE_PAYMENT_STAGES = [
  {
    name: `${PURCHASE_NEW_PROJECT_LABEL.OptionFee}*`,
    subName: PURCHASE_NEW_PROJECT_LABEL.OptionFeeSubtitle,
    timeline: '-',
    paymentPercentage: progressivePaymentConfig.option_fee,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.BalanceDownpayment,
    subName: PURCHASE_NEW_PROJECT_LABEL.BalanceDownpaymentSubtitle,
    timeline: '-',
    paymentPercentage: progressivePaymentConfig.balance_downpayment,
    timelineValue: 8,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.Foundation,
    subName: PURCHASE_NEW_PROJECT_LABEL.FoundationSubtitle,
    timeline: '6-9',
    paymentPercentage: progressivePaymentConfig.foundation_stage,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.Framework,
    subName: PURCHASE_NEW_PROJECT_LABEL.FrameworkSubtitle,
    timeline: '6-9',
    paymentPercentage: progressivePaymentConfig.framework_stage,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.Wall,
    subName: PURCHASE_NEW_PROJECT_LABEL.WallSubtitle,
    timeline: '3-6',
    paymentPercentage: progressivePaymentConfig.wall_stage,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.Ceiling,
    subName: PURCHASE_NEW_PROJECT_LABEL.CeilingSubtitle,
    timeline: '3-6',
    paymentPercentage: progressivePaymentConfig.ceiling_stage,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.Windows,
    subName: PURCHASE_NEW_PROJECT_LABEL.WindowsSubtitle,
    timeline: '3-6',
    paymentPercentage: progressivePaymentConfig.window_stage,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.CarPark,
    subName: PURCHASE_NEW_PROJECT_LABEL.CarParkSubtitle,
    timeline: '3-6',
    paymentPercentage: progressivePaymentConfig.carpark_stage,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.TOP,
    subName: PURCHASE_NEW_PROJECT_LABEL.TOPSubtitle,
    timeline: '9-12',
    paymentPercentage: progressivePaymentConfig.top_stage,
  },
  {
    name: PURCHASE_NEW_PROJECT_LABEL.CSC,
    subName: PURCHASE_NEW_PROJECT_LABEL.CSCSubtitle,
    timeline: '12',
    paymentPercentage: progressivePaymentConfig.csc_stage,
    timelineValue: 12,
  },
];

// Default loan details for Age 65
export const DEFAULT_LOAN_DETAILS_AGE_65 = {
  AGE: FinancingLoanType.sixtyFiveYearLoan,
  LOAN_TENURE: FINANCING_YEAR_65.LOAN_TENURE.MAX,
  LOAN_TENURE_LABEL: FINANCING_YEAR_65.LOAN_TENURE.SLIDER_LABELS,
  LOAN_TO_VALUE_MAX: FINANCING_YEAR_65.LTV.MAX,
  LOAN_TO_VALUE_DEFAULT: FINANCING_YEAR_65.LTV.DEFAULT,
  LOAN_TO_VALUE_LABEL: FINANCING_YEAR_65.LTV.SLIDER_LABELS,
};

// Default loan details for Age 75
export const DEFAULT_LOAN_DETAILS_AGE_75 = {
  AGE: FinancingLoanType.seventyFiveYearLoan,
  LOAN_TENURE: FINANCING_YEAR_75.LOAN_TENURE.MAX,
  LOAN_TENURE_LABEL: FINANCING_YEAR_75.LOAN_TENURE.SLIDER_LABELS,
  LOAN_TO_VALUE_MAX: FINANCING_YEAR_75.LTV.MAX,
  LOAN_TO_VALUE_DEFAULT: FINANCING_YEAR_75.LTV.DEFAULT,
  LOAN_TO_VALUE_LABEL: FINANCING_YEAR_75.LTV.SLIDER_LABELS,
};

export const LEGAL_FEE = {
  DEFAULT: calculatorConfig.legal_fee,
};

export const MORTGAGE_INTEREST_RATE = {
  DEFAULT: calculatorConfig.mortgage_interest_rate.private_resale.default,
  MIN: calculatorConfig.mortgage_interest_rate.private_resale.min,
  MAX: calculatorConfig.mortgage_interest_rate.private_resale.max,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.mortgage_interest_rate.private_resale.min,
    calculatorConfig.mortgage_interest_rate.private_resale.max,
    1,
  ),
  STEP: 0.01,
};

export enum NumberOfExistingLoans {
  NoLoan = '0',
  FirstLoan = '1',
  SecondLoanAndMore = '2+',
}

export const EXISTING_LOANS = [
  {
    value: NumberOfExistingLoans.NoLoan,
    label: NumberOfExistingLoans.NoLoan,
  },
  {
    value: NumberOfExistingLoans.FirstLoan,
    label: NumberOfExistingLoans.FirstLoan,
  },
  {
    value: NumberOfExistingLoans.SecondLoanAndMore,
    label: NumberOfExistingLoans.SecondLoanAndMore,
  },
];

export const LOAN_TYPE = {
  BANK: 'bank',
  NO_LOAN: 'no-loan',
};

export enum FOUNDATION_COMPLETION_PERIOD {
  EIGHT_WEEKS = '8-weeks',
  THREE_TO_SIX_MONTHS = '3-6-months',
  SIX_TO_NINE_MONTHS = '6-9-months',
}

export const getFoundationCompletionPeriodLabel = (value: FOUNDATION_COMPLETION_PERIOD) => {
  switch (value) {
    case FOUNDATION_COMPLETION_PERIOD.EIGHT_WEEKS: {
      return '8 Weeks';
    }

    case FOUNDATION_COMPLETION_PERIOD.THREE_TO_SIX_MONTHS: {
      return '3-6 Months';
    }

    case FOUNDATION_COMPLETION_PERIOD.SIX_TO_NINE_MONTHS: {
      return '6-9 Months';
    }

    default: {
      return '';
    }
  }
};

export const FOUNDATION_COMPLETION_OPTIONS = [
  { value: FOUNDATION_COMPLETION_PERIOD.EIGHT_WEEKS, label: '8 Weeks' },
  { value: FOUNDATION_COMPLETION_PERIOD.THREE_TO_SIX_MONTHS, label: '3-6 Months' },
  { value: FOUNDATION_COMPLETION_PERIOD.SIX_TO_NINE_MONTHS, label: '6-9 Months' },
];

export const MONTHLY_BREAKDOWN_COLORS = ['#9475EB', '#F25A73'];

// Breakdown chart labels
export const VALUATION_BREAKDOWN_LABELS = [
  PURCHASE_NEW_PROJECT_LABEL.Cash,
  PURCHASE_NEW_PROJECT_LABEL.CpfOaPerCash,
  PURCHASE_NEW_PROJECT_LABEL.LoanAmount,
];

export const MONTHLY_BREAKDOWN_LABELS = [
  PURCHASE_NEW_PROJECT_LABEL.Principal,
  PURCHASE_NEW_PROJECT_LABEL.Interest,
];
export const BALANCE_DOWNPAYMENT_PERCENT = calculatorConfig.balance_downpayment_in_percent;

type FinancingUpToConfig = {
  upTo65: number;
  upTo75: number;
};

// Map the number of loans to their max LTV
export const maxLTVMap: Record<string, FinancingUpToConfig> = {
  [NumberOfExistingLoans.NoLoan]: {
    upTo65: config.max_ltv_in_percent.private_residential.first_loan.upto_65_financing_year,
    upTo75: config.max_ltv_in_percent.private_residential.first_loan.upto_75_financing_year,
  },
  [NumberOfExistingLoans.FirstLoan]: {
    upTo65: config.max_ltv_in_percent.private_residential.second_loan.upto_65_financing_year,
    upTo75: config.max_ltv_in_percent.private_residential.second_loan.upto_75_financing_year,
  },
  [NumberOfExistingLoans.SecondLoanAndMore]: {
    upTo65:
      config.max_ltv_in_percent.private_residential.third_loan_and_onwards.upto_65_financing_year,
    upTo75:
      config.max_ltv_in_percent.private_residential.third_loan_and_onwards.upto_75_financing_year,
  },
};

export const minimumDownpaymentPayableInCashMap: Record<
  NumberOfExistingLoans,
  FinancingUpToConfig
> = {
  [NumberOfExistingLoans.NoLoan]: {
    upTo65:
      config.min_cash_downpayment_in_percent.private_residential.first_loan.upto_65_financing_year,
    upTo75:
      config.min_cash_downpayment_in_percent.private_residential.first_loan.upto_75_financing_year,
  },
  [NumberOfExistingLoans.FirstLoan]: {
    upTo65:
      config.min_cash_downpayment_in_percent.private_residential.second_loan.upto_65_financing_year,
    upTo75:
      config.min_cash_downpayment_in_percent.private_residential.second_loan.upto_75_financing_year,
  },
  [NumberOfExistingLoans.SecondLoanAndMore]: {
    upTo65:
      config.min_cash_downpayment_in_percent.private_residential.third_loan_and_onwards
        .upto_65_financing_year,
    upTo75:
      config.min_cash_downpayment_in_percent.private_residential.third_loan_and_onwards
        .upto_75_financing_year,
  },
};
